import { ENV_CONFIG } from 'src/bin/env.config'
import { Salon } from '../models/salon/salon.model'
import { Stylist } from '../models/stylist/stylist.model'

/**
 * Preserve explicit international country codes. Bare ten-digit numbers use
 * the North American default; omit incomplete numbers rather than inventing one.
 */
export function toE164Phone (raw: string | null | undefined): string {
  const value = (raw || '').trim()
  const digits = value.replace(/\D/g, '')
  if (value.startsWith('+') && /^[1-9]\d{6,14}$/.test(digits)) return `+${digits}`
  if (/^1\d{10}$/.test(digits)) return `+${digits}`
  return /^\d{10}$/.test(digits) ? `+1${digits}` : ''
}

/**
 * Job title shown to support, with a junk filter:
 * titles of 3 characters or fewer are noise, not titles.
 */
export function getFilteredJobTitle (user: Stylist): string | undefined {
  const title = user.getTitle()
  return title && title.length > 3 ? title : undefined
}

/**
 * Days left on the free trial; only meaningful while is_on_free_trial is True,
 * which is the attribute that qualifies it.
 *
 * An expired trial is still planID 1, so the value stays NEGATIVE on purpose —
 * support reads "expired 45 days ago" off it, and flooring it to 0 would make
 * an expired trial indistinguishable from one ending today. Only two cases are
 * forced to 0: a salon that is not on a trial at all, and a salon missing
 * trialLength, where the subtraction is NaN — which JSON sends as null and
 * would give the attribute a second type in the workspace.
 */
export function chatTrialDaysLeft (salon: Salon): number {
  const raw = salon.trialDaysLeft()
  return salon.isOnFreeTrial() && Number.isFinite(raw) ? raw : 0
}

/**
 * Intercom carries these as text, so every boolean in the payload is the
 * string 'True' or 'False' — the convention the attribute set has always
 * used. Keep new flags going through here so one of them can't drift into a
 * real boolean and split the attribute's type in the workspace.
 */
export function flag (on: boolean): string {
  return on ? 'True' : 'False'
}

/**
 * An ISO date string for support to read, or '' when the value is missing or
 * unparseable — never a half-formed date.
 */
export function toIsoDate (value: string | Date | null | undefined): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

/**
 * Facts about where the app is running, which neither the stylist nor the
 * salon record knows. Supplied by the caller (IntercomService) because this
 * module stays free of Angular injection.
 */
/** Where the app is running. */
export type ChatPlatform = 'ios' | 'android' | 'web'

export interface ChatRuntimeContext {
  platform: ChatPlatform
}

/**
 * Custom attributes sent with Intercom's messenger boot so support sees the
 * stylist's and salon's context. IntercomService adds `salon_creation_date`
 * and `job_title` itself.
 *
 * SalonMonster-owned custom attribute names consistently use snake_case.
 */
export function buildChatCustomAttributes (
  user: Stylist,
  salon: Salon,
  runtime: ChatRuntimeContext
): Record<string, unknown> {
  return {
    'stylist_id': user.getID(),
    'salon_id': salon.getId(),
    'app_version': salon.getAppVersion() || '',
    'app_build_number': ENV_CONFIG.appVersion || '',
    'has_calendar': flag(user.getCalendar() === 1),
    'is_administrator': flag(user.isAdministrator()),
    'is_owner': flag(user.isOwner()),
    'can_view_all_calendars': flag(user.getViewAllCalendars() === 1),
    'can_view_all_clients': flag(user.getViewAllClients() === 1),
    'has_settings_control': flag(user.getSettingsControl() === 1),
    'requires_appt_confirmation': flag(user.getConfirmApptsManually() === 1),
    'salon_website': salon.getWebsite() || '',
    'salon_timezone': salon.getTimeZone() || '',
    'salon_country': salon.getCountry() ? salon.getCountry().getCountryName() : '',
    'salon_phone': toE164Phone(salon.getPhone()),
    'read_only': flag(salon.isReadOnly()),
    'has_stripe_account': flag(salon.hasStripeAccount()),
    'stylist_pronouns': user.getReadablePronouns(),
    'is_active': flag(user.isActive()),
    'stats_link': salon.getId()
      ? `https://salonmonster.com/internal/stats_detail.php?id=${salon.getId()}`
      : '',
    'salon_booking_link': salon.getSubDomain()
      ? `${salon.getSubDomain()}.salonmonster.com`
      : '',
    'bookings_count': user.getBookingsCount() || 0,
    'clients_count': user.getClientsCount() || 0,
    // Service definitions are not loaded in the login model. The API signs the real services_count.
    'staff_phone': toE164Phone(user.getPhone2()),
    'stylist_joined': toIsoDate(user.getCreationDate()),
    // Where the app runs. appVersion/releaseTrack route the WEB app only — a
    // native build ships its compiled line to everyone — so without this,
    // "fine on desktop, broken on my phone" is invisible to support.
    'platform': runtime.platform,
    'release_track': salon.getReleaseTrack() || '',
    // read_only is true for an admin lock OR an expired free trial; these two
    // say which, because the answers are opposite.
    'is_on_free_trial': flag(salon.isOnFreeTrial()),
    'trial_days_left': chatTrialDaysLeft(salon),
    'plan_id': salon.getPlanID(),
    'currency': salon.getCurrency() || '',
    'can_view_reports': flag(user.getCanViewReports() === 1),
    'can_connect_accounts': flag(user.getCanConnectAccountsEffective() === 1),
    'pos_enabled': flag(salon.getEnablePOS() === 1),
    'requires_card_on_file': flag(salon.getRequireCardOnFile()),
    'no_show_percentage': salon.getNoShowPercentage()
  }
}
